import { Button } from '@blueprintjs/core';
import { flexRender } from '@tanstack/react-table';
import ABRCodeDrawerButton from 'Components/SiteDrawerContainer/ABRCodeDrawerButton';
import AccountDrawerButton from 'Components/SiteDrawerContainer/AccountDrawerButton';
import { useIsVisible } from 'Lib/hooks/useIsVisible';
import PropTypes from 'prop-types';
import styles from './ReportTable.module.css';
import {
  getCellClassname,
  getCellKey,
  getGroupingRowClassname,
  getGroupingRowKey,
  getLeafRowKey,
  isVisibleOptions,
} from './reportUtil';

const getExpandButtonForRow = (row) => {
  return (
    row.getCanExpand() && (
      <Button
        className={styles.expand_button}
        minimal
        icon={row.getIsExpanded() ? 'chevron-up' : 'chevron-down'}
        onClick={row.getToggleExpandedHandler()}
      />
    )
  );
};

const getGroupingRowComponent = (row, index, numVisibleCols) => {
  let isGroupStart = row.depth === 1 && !(index === 0 && row.index === 0);

  return row.groupingColumnId === 'abr' ? (
    <tr key={getGroupingRowKey(row, index)} id={getGroupingRowKey(row, index)}>
      <td id={getGroupingRowKey(row)} className={styles.abr_header}>
        {getExpandButtonForRow(row)}
      </td>
      {row.getVisibleCells().map(
        (cell) =>
          cell.column.columnDef.id === 'abr' && (
            <td
              id={getCellKey(cell, 'HEADER')}
              key={getCellKey(cell, 'HEADER')}
              className={`${styles.abr_header} ${styles.abr_code}`}>
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          ),
      )}
      <td
        id={`${row.id}_group`}
        className={styles.abr_header}
        colSpan={numVisibleCols - 2}
        onDoubleClick={row.getCanExpand() && row.getToggleExpandedHandler()}>
        <ABRCodeDrawerButton
          label={row.original.expense_code?.abr_code?.description}
          abrCodeId={row.original.expense_code?.abr_code_fk}
        />
      </td>
    </tr>
  ) : (
    <tr
      key={getGroupingRowKey(row, index)}
      id={getGroupingRowKey(row, index, isGroupStart)}>
      {row.getVisibleCells().map(
        (cell) =>
          cell.column.columnDef.id === row.groupingColumnId && (
            <td
              key={cell.id}
              id={getCellKey(cell, isGroupStart ? 'BEGIN' : '')}
              className={getGroupingRowClassname(row)}
              onDoubleClick={
                row.getCanExpand() && row.getToggleExpandedHandler()
              }
              colSpan={numVisibleCols}>
              <div className={styles.header_wrapper}>
                {getExpandButtonForRow(row)}
                {row.groupingColumnId === 'activity' ? (
                  <AccountDrawerButton
                    label={
                      row.original.charge_account?.code ||
                      row.original.assigned_charge_account_id
                    }
                    accountId={row.original.assigned_charge_account_id}
                  />
                ) : (
                  flexRender(cell.column.columnDef.cell, cell.getContext())
                )}
              </div>
            </td>
          ),
      )}
    </tr>
  );
};

const getLeafRowComponent = (row, index, markAsGroupEnd) => {
  let markAsGroup =
    markAsGroupEnd || (row.id.includes('category:EXPENSE') && row.depth === 1);
  // For grand total rows, want to merge all columns up to 'description'
  // These rows all have row depth as less than 4.
  let descriptionColumnIndex;

  return (
    <tr
      id={getLeafRowKey(row, markAsGroup)}
      key={getLeafRowKey(row, markAsGroup)}>
      {row
        .getVisibleCells()
        .filter((cell) => !cell.column.getIsGrouped())
        .map((cell, colIndex) => {
          if (cell.column.id === 'description') {
            descriptionColumnIndex = colIndex;
          }
          return !cell.getIsAggregated() ||
            row.depth > 3 ||
            // If aggregated and one of the 'grand' total rows, need to check if we are beyond the description column
            (descriptionColumnIndex && colIndex >= descriptionColumnIndex) ? (
            <td
              key={cell.id}
              id={getCellKey(cell, markAsGroup ? 'END' : '')}
              className={getCellClassname(row, cell, index)}
              colSpan={
                cell.getIsAggregated() &&
                row.depth < 4 &&
                cell.column.id === 'description'
                  ? colIndex + 1
                  : 1
              }>
              {cell.getIsAggregated() ? (
                <div className={row.depth < 4 ? styles.agg_header_wrapper : ''}>
                  {flexRender(
                    cell.column.columnDef.aggregatedCell ??
                      cell.column.columnDef.cell,
                    cell.getContext(),
                  )}
                </div>
              ) : cell.getIsPlaceholder() ? null : (
                flexRender(cell.column.columnDef.cell, cell.getContext())
              )}
            </td>
          ) : null;
        })}
    </tr>
  );
};

const renderRow = (rowInfo, numVisibleCols) => {
  let { row, index, type, markAsGroupEnd = false } = rowInfo;
  if (type === 'LEAF') {
    return getLeafRowComponent(row, index, markAsGroupEnd);
  } else if (type === 'GROUPING') {
    return getGroupingRowComponent(row, index, numVisibleCols);
  }
};
const OrgTableBody = ({ orgReportInfoObject, index, numVisibleCols }) => {
  let { orgRef, orgRows } = orgReportInfoObject;
  const [isVisible] = useIsVisible(orgRef, isVisibleOptions);

  return (
    <tbody ref={orgRef} key={index}>
      {orgRows.map((rowInfo, index) => {
        return isVisible || index < 3 ? (
          renderRow(rowInfo, numVisibleCols)
        ) : (
          <tr key={index}>
            <td
              className={index % 2 === 0 ? styles.gray_background_cell : ''}
              colSpan={numVisibleCols}
              style={{ height: rowInfo.height, margin: 4 }}></td>
          </tr>
        );
      })}
    </tbody>
  );
};

OrgTableBody.propTypes = {
  orgReportInfoObject: PropTypes.shape({
    orgRef: PropTypes.object,
    totalHeightForGroup: PropTypes.number,
    orgRows: PropTypes.array,
  }),
  index: PropTypes.number,
  numVisibleCols: PropTypes.number,
};

export default OrgTableBody;
